import type { PageLayout } from "@/components/page-chrome/page-layout"
import { searchPages } from "@/components/search/search-pages"
import type { PageModule } from "@/lib/page-rendering/page-registry"
import { educatorPages } from "@/pages/educators/page-chrome"
import { learnerPages } from "@/pages/learners/page-chrome"
import { userPages } from "@/pages/users/pages"

// The app's page map: the one eager glob of every page. It lives here, at
// the composition root, the only module that knows every domain. Nothing a
// page imports reaches this module, so the map closes no import cycle.
const modules = import.meta.glob<PageModule>("../pages/**/*.page.tsx", {
  eager: true,
})

const PAGE_PATH = /^\.\.\/pages\/(.+)\.page\.tsx$/

const pathOf = (name: string) => `../pages/${name}.page.tsx`

// Every page's Inertia component name, as the glob found them.
export const pageNames: readonly string[] = Object.keys(modules).map(
  (path) => path.match(PAGE_PATH)![1],
)

// A page's module by its component name. Raises on a name no page has.
export function pageModule(name: string): PageModule {
  const path = pathOf(name)
  if (!Object.hasOwn(modules, path)) {
    throw new Error(`Missing page component ${name}.page.tsx`)
  }
  return modules[path]
}

// The domain registries, every page's static facts. A page is in exactly
// one of them.
const registries: Readonly<
  Record<string, Readonly<Record<string, { layout: PageLayout }>>>
> = {
  educators: educatorPages,
  learners: learnerPages,
  search: searchPages,
  users: userPages,
}

// The layout a page's registry entry declares, read before the page renders.
// Raises on a page in no registry, or in more than one.
export function pageLayout(name: string): PageLayout {
  const holders = Object.entries(registries).filter(([, pages]) =>
    Object.hasOwn(pages, name),
  )
  if (holders.length === 0) {
    throw new Error(`${name} is in no page registry`)
  }
  if (holders.length > 1) {
    const names = holders.map(([registry]) => registry).join(" and ")
    throw new Error(`${name} is in the ${names} page registries`)
  }
  return holders[0][1][name].layout
}
